// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ✏️ EXERCISE EDITOR COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  createExercise,
  updateExercise,
  uploadExerciseImage,
  removeImageFromExercise,
} from '../../services/exercises';
import type { Exercise, ExerciseCategory, Difficulty, ScoringType, ScoringConfig } from '../../types';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import './ExerciseEditor.css';

interface ExerciseEditorProps {
  exercise?: Exercise;
  clubId?: number;
  isGlobal?: boolean;
  onSave: (exercise: Exercise) => void;
  onCancel: () => void;
  onDelete?: (exerciseId: number) => void;
}

export const ExerciseEditor: React.FC<ExerciseEditorProps> = ({
  exercise,
  clubId: propClubId,
  isGlobal: propIsGlobal = false,
  onSave,
  onCancel,
  onDelete,
}) => {
  const { player, isGlobalAdmin } = useAuth();
  const { t } = useTranslation();

  // Scope selection
  const [scope, setScope] = useState<'personal' | 'club' | 'global'>(
    exercise?.is_global ? 'global' : exercise?.club_id ? 'club' : 'personal'
  );
  const [selectedClubId, setSelectedClubId] = useState<number | undefined>(exercise?.club_id || propClubId);
  const [userClubs, setUserClubs] = useState<any[]>([]);

  const [title, setTitle] = useState(exercise?.title || '');
  const [description, setDescription] = useState(exercise?.description || '');
  const [instructions, setInstructions] = useState(exercise?.instructions || '');
  const [category, setCategory] = useState<ExerciseCategory | ''>(exercise?.category || '');
  const [difficulty, setDifficulty] = useState<Difficulty | ''>(exercise?.difficulty || '');
  const [scoringType, setScoringType] = useState<ScoringType | ''>(exercise?.scoring_type || '');
  const [scoringConfig, setScoringConfig] = useState<ScoringConfig>(exercise?.scoring_config || {});
  const [images, setImages] = useState<string[]>(exercise?.images || []);
  const [isPublic, setIsPublic] = useState(exercise?.is_public || false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Load user clubs
  useEffect(() => {
    const loadUserClubs = async () => {
      const { getUserClubMemberships } = await import('../../services/clubs');
      const memberships = await getUserClubMemberships();
      setUserClubs(memberships);
      
      // Set first club as default if no club selected
      if (!selectedClubId && memberships.length > 0) {
        setSelectedClubId(memberships[0].club_id);
      }
    };
    
    loadUserClubs();
  }, []);

  // Update scope when switching
  const handleScopeChange = (newScope: 'personal' | 'club' | 'global') => {
    setScope(newScope);
    
    if (newScope === 'personal') {
      setSelectedClubId(undefined);
    } else if (newScope === 'club' && !selectedClubId && userClubs.length > 0) {
      setSelectedClubId(userClubs[0].club_id);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !exercise?.id) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(file =>
        uploadExerciseImage(exercise.id, file)
      );
      const urls = await Promise.all(uploadPromises);
      setImages([...images, ...urls]);
    } catch (err) {
      console.error('Upload failed:', err);
      alert(t('training.exercises.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (url: string) => {
    if (!exercise?.id) {
      setImages(images.filter(img => img !== url));
      return;
    }

    try {
      await removeImageFromExercise(exercise.id, url);
      setImages(images.filter(img => img !== url));
    } catch (err) {
      console.error('Remove failed:', err);
      alert(t('training.exercises.removeFailed'));
    }
  };

  const handleSave = async () => {
    if (!player || !title.trim()) {
      alert(t('training.exercises.titleRequired'));
      return;
    }

    setSaving(true);
    try {
      const exerciseData = {
        created_by: player.id,
        club_id: scope === 'club' ? selectedClubId : undefined,
        is_global: scope === 'global',
        title: title.trim(),
        description: description.trim() || undefined,
        instructions: instructions.trim() || undefined,
        images,
        category: category || undefined,
        difficulty: difficulty || undefined,
        scoring_type: scoringType || undefined,
        scoring_config: Object.keys(scoringConfig).length > 0 ? scoringConfig : undefined,
        is_public: isPublic,
      };

      let result: Exercise;
      if (exercise) {
        result = await updateExercise(exercise.id, exerciseData);
      } else {
        result = await createExercise(exerciseData);
      }

      onSave(result);
    } catch (err) {
      console.error('Save failed:', err);
      alert(t('training.exercises.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const updateScoringConfig = (key: string, value: any) => {
    setScoringConfig({ ...scoringConfig, [key]: value });
  };

  return (
    <div className="exercise-editor">
      <div className="editor-header">
        <h2>
          {exercise 
            ? t('training.exercises.edit') 
            : scope === 'global'
            ? t('training.exercises.createGlobal') 
            : t('training.exercises.create')
          }
        </h2>
      </div>

      <div className="editor-form">
        {/* Scope Selection - nur beim Erstellen */}
        {!exercise && (
          <div className="form-group">
            <label>{t('training.exercises.scope')} *</label>
            <div className="scope-selector">
              <button
                type="button"
                className={scope === 'personal' ? 'active' : ''}
                onClick={() => handleScopeChange('personal')}
              >
                👤 {t('training.exercises.personal')}
              </button>
              {userClubs.length > 0 && (
                <button
                  type="button"
                  className={scope === 'club' ? 'active' : ''}
                  onClick={() => handleScopeChange('club')}
                >
                  🏛️ {t('training.exercises.club')}
                </button>
              )}
              {isGlobalAdmin && (
                <button
                  type="button"
                  className={scope === 'global' ? 'active' : ''}
                  onClick={() => handleScopeChange('global')}
                >
                  🌍 {t('training.exercises.global')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Club Selection - nur bei Club-Scope */}
        {!exercise && scope === 'club' && userClubs.length > 0 && (
          <div className="form-group">
            <label>{t('training.exercises.selectClub')} *</label>
            <select
              value={selectedClubId || ''}
              onChange={(e) => setSelectedClubId(Number(e.target.value))}
            >
              {userClubs.map(membership => (
                <option key={membership.club_id} value={membership.club_id}>
                  {membership.club_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Title */}
        <div className="form-group">
          <label>{t('training.exercises.title')} *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('training.exercises.titlePlaceholder')}
            maxLength={200}
          />
        </div>

        {/* Description */}
        <div className="form-group">
          <label>{t('training.exercises.description')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('training.exercises.descriptionPlaceholder')}
            rows={3}
          />
        </div>

        {/* Instructions */}
        <div className="form-group">
          <label>{t('training.exercises.instructions')}</label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder={t('training.exercises.instructionsPlaceholder')}
            rows={5}
          />
        </div>

        {/* Category */}
        <div className="form-group">
          <label>{t('training.exercises.category')}</label>
          <select value={category} onChange={(e) => setCategory(e.target.value as ExerciseCategory)}>
            <option value="">{t('training.exercises.selectCategory')}</option>
            <option value="potting">{t('training.category.potting')}</option>
            <option value="safety">{t('training.category.safety')}</option>
            <option value="positioning">{t('training.category.positioning')}</option>
            <option value="break_building">{t('training.category.break_building')}</option>
            <option value="cue_ball_control">{t('training.category.cue_ball_control')}</option>
            <option value="technique">{t('training.category.technique')}</option>
          </select>
        </div>

        {/* Difficulty */}
        <div className="form-group">
          <label>{t('training.exercises.difficulty')}</label>
          <select value={difficulty} onChange={(e) => setDifficulty(e.target.value as Difficulty)}>
            <option value="">{t('training.exercises.selectDifficulty')}</option>
            <option value="beginner">{t('training.difficulty.beginner')}</option>
            <option value="intermediate">{t('training.difficulty.intermediate')}</option>
            <option value="advanced">{t('training.difficulty.advanced')}</option>
            <option value="expert">{t('training.difficulty.expert')}</option>
          </select>
        </div>

        {/* Scoring Type */}
        <div className="form-group">
          <label>{t('training.exercises.scoringType')}</label>
          <select value={scoringType} onChange={(e) => setScoringType(e.target.value as ScoringType)}>
            <option value="">{t('training.exercises.selectScoringType')}</option>
            <option value="points">{t('training.scoring.points')}</option>
            <option value="balls_potted">{t('training.scoring.balls_potted')}</option>
            <option value="success_rate">{t('training.scoring.success_rate')}</option>
            <option value="time">{t('training.scoring.time')}</option>
            <option value="attempts">{t('training.scoring.attempts')}</option>
            <option value="custom">{t('training.scoring.custom')}</option>
          </select>
        </div>

        {/* Scoring Config */}
        {scoringType === 'points' && (
          <div className="scoring-config">
            <div className="form-group">
              <label>{t('training.scoring.maxPoints')}</label>
              <input
                type="number"
                value={scoringConfig.max_points || ''}
                onChange={(e) => updateScoringConfig('max_points', parseInt(e.target.value))}
                min="0"
              />
            </div>
            <div className="form-group">
              <label>{t('training.scoring.passThreshold')}</label>
              <input
                type="number"
                value={scoringConfig.pass_threshold || ''}
                onChange={(e) => updateScoringConfig('pass_threshold', parseInt(e.target.value))}
                min="0"
              />
            </div>
          </div>
        )}

        {scoringType === 'balls_potted' && (
          <div className="scoring-config">
            <div className="form-group">
              <label>{t('training.scoring.totalBalls')}</label>
              <input
                type="number"
                value={scoringConfig.total_balls || ''}
                onChange={(e) => updateScoringConfig('total_balls', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="form-group">
              <label>{t('training.scoring.target')}</label>
              <input
                type="number"
                value={scoringConfig.target || ''}
                onChange={(e) => updateScoringConfig('target', parseInt(e.target.value))}
                min="0"
              />
            </div>
          </div>
        )}

        {/* Images */}
        <div className="form-group">
          <label>{t('training.exercises.images')}</label>
          <div className="image-upload">
            <div className="image-grid">
              {images.map((url, idx) => (
                <div key={idx} className="image-preview">
                  <img src={url} alt={`Exercise ${idx + 1}`} />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(url)}
                    className="remove-image"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              disabled={uploading || !exercise}
            />
            {uploading && <p>{t('common.uploading')}</p>}
            {!exercise && <p className="hint">{t('training.exercises.saveFirstToUpload')}</p>}
          </div>
        </div>

        {/* Public */}
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            {scope === 'club'
              ? t('training.exercises.publicInClub') 
              : scope === 'global'
              ? t('training.exercises.publicGlobal') 
              : t('training.exercises.publicPersonal')
            }
          </label>
        </div>
      </div>

      <div className="editor-actions">
        <div className="editor-actions-left">
          {exercise && onDelete && (
            <button 
              onClick={() => setShowDeleteModal(true)} 
              disabled={saving}
              className="btn-danger"
            >
              🗑️ {t('common.delete')}
            </button>
          )}
        </div>
        <div className="editor-actions-right">
          <button onClick={onCancel} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving || !title.trim()} className="btn-primary">
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {exercise && onDelete && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          title={t('training.exercises.deleteTitle')}
          message={t('training.exercises.deleteWarning')}
          onConfirm={() => {
            setShowDeleteModal(false);
            onDelete(exercise.id);
          }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

